/**
 * ParentChallengeJourney.js — read-only mirror of a linked teen's
 * 20-challenge journey. View-only: no practice buttons, no actions.
 */
import React, { useEffect, useState } from 'react';
import { ParentShell } from '../components/parent/ParentShell';
import { apiClient } from '../api/client';
import {
  Loader2, CheckCircle2, Heart, Eye, Footprints, Users, UserPlus, BookOpen,
  Gift, Sparkles, GraduationCap, MessageCircle, PenLine, Home, Palette,
  WifiOff, Smartphone, Smile, HeartHandshake, Megaphone, Wrench, HelpingHand,
  Flag, Trophy, Gem,
} from 'lucide-react';

const THEME_ICONS = {
  self_care: [Heart, 'text-rose-500', 'bg-rose-100'],
  observation: [Eye, 'text-sky-500', 'bg-sky-100'],
  physical_presence: [Footprints, 'text-emerald-500', 'bg-emerald-100'],
  physical_meetup: [Users, 'text-orange-500', 'bg-orange-100'],
  inviting_connection: [UserPlus, 'text-fuchsia-500', 'bg-fuchsia-100'],
  reading_stillness: [BookOpen, 'text-indigo-500', 'bg-indigo-100'],
  secret_kindness: [Gift, 'text-pink-500', 'bg-pink-100'],
  self_reflection: [Sparkles, 'text-violet-500', 'bg-violet-100'],
  skill_sharing: [GraduationCap, 'text-blue-500', 'bg-blue-100'],
  stranger_interaction: [MessageCircle, 'text-teal-500', 'bg-teal-100'],
  written_communication: [PenLine, 'text-cyan-600', 'bg-cyan-100'],
  family_connection: [Home, 'text-amber-500', 'bg-amber-100'],
  creative_making: [Palette, 'text-purple-500', 'bg-purple-100'],
  digital_detox_extended: [WifiOff, 'text-slate-500', 'bg-slate-100'],
  digital_detox_l1: [Smartphone, 'text-lime-600', 'bg-lime-100'],
  gratitude_expression: [Smile, 'text-yellow-500', 'bg-yellow-100'],
  community_kindness: [HeartHandshake, 'text-red-500', 'bg-red-100'],
  public_contribution: [Megaphone, 'text-orange-600', 'bg-orange-100'],
  home_contribution: [Wrench, 'text-stone-500', 'bg-stone-100'],
  community_service: [HelpingHand, 'text-green-600', 'bg-green-100'],
};

const ChallengeCard = ({ c }) => {
  const [Icon, iconColor, iconBg] = THEME_ICONS[c.theme_key] || [Sparkles, 'text-violet-500', 'bg-violet-100'];
  const done = c.status === 'completed';
  const nftZone = c.sequence > 10;
  const cardStyle = done
    ? 'bg-green-50 border-green-300 shadow-md'
    : c.is_current
      ? 'bg-white border-purple-500 ring-2 ring-purple-200 shadow-lg'
      : nftZone
        ? 'bg-white border-amber-200 shadow-md'
        : 'bg-white border-gray-300 shadow-md';
  return (
    <div className={`relative rounded-2xl border-2 p-4 ${cardStyle}`} data-testid={`parent-journey-card-${c.sequence}`}>
      {nftZone && (
        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow" title="Completing this challenge mints an NFT into the Vault">
          <Gem size={12} className="text-white" />
        </span>
      )}
      <div className="flex items-center justify-between mb-3">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
          done ? 'bg-green-500 text-white' : c.is_current ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
        }`}>
          {c.sequence}
        </span>
        {done ? (
          <CheckCircle2 className="text-green-500" size={22} />
        ) : (
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
            <Icon className={iconColor} size={18} />
          </span>
        )}
      </div>
      <p className={`font-semibold text-sm ${done ? 'text-green-800' : 'text-gray-900'}`}>{c.theme_label}</p>
      {c.is_current && (
        <p className="mt-1 text-xs font-semibold text-purple-600">Up next for them ✦</p>
      )}
      {done && c.completed_at && (
        <p className="mt-1 text-xs text-green-600">Done {String(c.completed_at).slice(0, 10)}</p>
      )}
    </div>
  );
};

const ParentChallengeJourney = () => {
  const [teens, setTeens] = useState(null);
  const [selectedTeen, setSelectedTeen] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);

  useEffect(() => {
    apiClient.request('/api/parent/custom-dashboard')
      .then(res => {
        const t = res?.teens || [];
        setTeens(t);
        if (t.length > 0) setSelectedTeen(t[0].teen_id);
      })
      .catch(() => setTeens([]));
  }, []);

  useEffect(() => {
    if (!selectedTeen) return;
    setLoadingJourney(true);
    setJourney(null);
    apiClient.request(`/api/schools/journey/parent-view/${selectedTeen}`)
      .then(setJourney)
      .catch(() => setJourney({ has_journey: false, challenges: [] }))
      .finally(() => setLoadingJourney(false));
  }, [selectedTeen]);

  const pct = journey?.total_count ? Math.round((journey.completed_count / journey.total_count) * 100) : 0;
  const current = journey?.challenges?.find(c => c.is_current);
  const firstTen = (journey?.challenges || []).filter(c => c.sequence <= 10);
  const nftTen = (journey?.challenges || []).filter(c => c.sequence > 10);

  return (
    <ParentShell activeId="challenges" title="Challenge Journey">
      <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto" data-testid="parent-challenge-journey-page">
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <Flag className="text-purple-600" size={26} /> Their Challenge Journey
          </h2>
          <p className="text-gray-600 text-sm">
            A view-only window into your teen&apos;s 20-challenge journey — you can watch the progress, they do the doing.
          </p>
        </div>

        {teens === null ? (
          <div className="flex items-center justify-center py-24" data-testid="parent-journey-loading">
            <Loader2 className="animate-spin text-purple-500" size={32} />
          </div>
        ) : teens.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-200" data-testid="parent-journey-no-teens">
            <Flag className="mx-auto mb-4 text-purple-400" size={36} />
            <p className="font-semibold text-gray-900 mb-1">No linked teens yet</p>
            <p className="text-sm text-gray-600">Once a teen is linked to your account, their journey appears here.</p>
          </div>
        ) : (
          <>
            {teens.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="parent-journey-teen-picker">
                {teens.map(t => (
                  <button key={t.teen_id}
                    onClick={() => setSelectedTeen(t.teen_id)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${
                      selectedTeen === t.teen_id
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300'
                    }`}
                    data-testid={`parent-journey-teen-${t.teen_id}`}>
                    {t.teen_first_name || 'Teen'}
                  </button>
                ))}
              </div>
            )}

            {loadingJourney ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="animate-spin text-purple-500" size={32} />
              </div>
            ) : !journey?.has_journey ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200" data-testid="parent-journey-not-started">
                <Flag className="mx-auto mb-4 text-purple-400" size={36} />
                <p className="font-semibold text-gray-900 mb-1">
                  {journey?.teen_name || 'Your teen'}&apos;s journey hasn&apos;t started yet
                </p>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  Their personalised 20-challenge journey is created once they complete their registration questions.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-gray-900" data-testid="parent-journey-progress-label">
                      {journey.teen_name}: {journey.completed_count} of {journey.total_count} completed
                    </p>
                    <p className="text-sm text-purple-600 font-semibold">{pct}%</p>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                      style={{ width: `${Math.max(pct, 3)}%` }} />
                  </div>
                  {current ? (
                    <p className="mt-3 text-sm text-gray-700">
                      <span className="font-semibold text-purple-700">Up next for them:</span>{' '}
                      Challenge No {current.sequence} — {current.theme_label}
                    </p>
                  ) : (
                    <p className="mt-3 text-sm font-semibold text-green-700 flex items-center gap-2">
                      <Trophy size={16} /> Every challenge completed — incredible!
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="parent-journey-grid">
                  {firstTen.map(c => <ChallengeCard key={c.sequence} c={c} />)}
                </div>

                {nftTen.length > 0 && (
                  <div className="my-8 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-400 to-pink-500 p-[2px] shadow-lg">
                    <div className="rounded-2xl bg-white/95 px-5 py-4 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
                      <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow flex-shrink-0">
                        <Gem size={22} className="text-white" />
                      </span>
                      <div>
                        <p className="font-bold text-gray-900">The NFT Rewards Zone starts here 💎</p>
                        <p className="text-sm text-gray-600">
                          From Challenge No 11 onwards, every completion mints a real NFT into their Vault.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="parent-journey-nft-grid">
                  {nftTen.map(c => <ChallengeCard key={c.sequence} c={c} />)}
                </div>

                <p className="mt-8 text-center text-sm text-gray-500">
                  View-only for parents — the bravery is all theirs. 🏁
                </p>
              </>
            )}
          </>
        )}
      </div>
    </ParentShell>
  );
};

export default ParentChallengeJourney;
